import { Injectable } from '@angular/core';
import {
  getFirestore,
  collection,
  query,
  where,
  getDocs,
  doc,
  getDoc,
  deleteDoc,
  setDoc,
  limit
} from '@angular/fire/firestore';

interface Employee {
  id: string;
  email: string;
  fullName: string;
  handler: string;
  phone: string;
  profilePicture: string;
  department?: string;
  position?: string;
  status?: 'active' | 'inactive' | 'on_leave' | 'terminated';
  joinDate?: string;
  createdAt?: string;
}

@Injectable({
  providedIn: 'root'
})
export class EmployeesService {

  constructor() { }

  async getEmployeesByHandler(userHandler: string): Promise<Employee[]> {
    console.log('🔍 [EmployeesService] === GETTING EMPLOYEES FOR HANDLER ===');
    console.log('🔍 [EmployeesService] User handler:', userHandler);
    
    try {
      const db = getFirestore();
      const employees: Employee[] = [];
      
      // First, search in users collection to get the user's email/details
      console.log('🔍 [EmployeesService] Searching for user with ID:', userHandler);
      const usersRef = collection(db, 'users');
      const userQuery = query(usersRef, where('uid', '==', userHandler));
      const userSnapshot = await getDocs(userQuery);
      
      let userEmail = '';
      if (!userSnapshot.empty) {
        const userData = userSnapshot.docs[0].data();
        userEmail = userData['email'] || '';
        console.log('✅ [EmployeesService] Found user with email:', userEmail);
      }
      
      // Now search for cards with the user's email or uid as handler
      console.log('🔍 [EmployeesService] Searching for cards with handler:', userHandler, 'or email:', userEmail);
      const cardsRef = collection(db, 'data/digitalcard/cards');
      
      // Try multiple queries to find the user's card
      let cardDoc = null;
      let cardId = '';
      
      // First try with the userHandler (uid)
      const cardQuery1 = query(cardsRef, where('handler', '==', userHandler));
      const cardSnapshot1 = await getDocs(cardQuery1);
      
      if (!cardSnapshot1.empty) {
        cardDoc = cardSnapshot1.docs[0];
        cardId = cardDoc.id;
        console.log('✅ [EmployeesService] Found card with handler (uid):', cardId);
      } else if (userEmail) {
        // Try with email if uid didn't work
        const cardQuery2 = query(cardsRef, where('handler', '==', userEmail));
        const cardSnapshot2 = await getDocs(cardQuery2);
        
        if (!cardSnapshot2.empty) {
          cardDoc = cardSnapshot2.docs[0];
          cardId = cardDoc.id;
          console.log('✅ [EmployeesService] Found card with handler (email):', cardId);
        } else {
          // Try searching in card data for email field
          const cardQuery3 = query(cardsRef, where('email', '==', userEmail));
          const cardSnapshot3 = await getDocs(cardQuery3);
          
          if (!cardSnapshot3.empty) {
            cardDoc = cardSnapshot3.docs[0];
            cardId = cardDoc.id;
            console.log('✅ [EmployeesService] Found card with email field:', cardId);
          }
        }
      }
      
      if (!cardDoc) {
        console.warn('⚠️ [EmployeesService] No card found for user. Trying to list all cards to debug...');
        
        // Debug: List first few cards to see the structure
        const allCardsSnapshot = await getDocs(query(cardsRef, limit(5)));
        console.log('🔍 [EmployeesService] Found', allCardsSnapshot.size, 'total cards');
        
        allCardsSnapshot.docs.forEach((doc, index) => {
          const data = doc.data();
          console.log(`🔍 [EmployeesService] Card ${index + 1}:`, {
            id: doc.id,
            handler: data['handler'],
            email: data['email'],
            name: data['name'] || data['fullName']
          });
        });
        
        return [];
      }
      
      // Now get all subcards for this card
      console.log('🔍 [EmployeesService] Searching for subcards under card:', cardId);
      const subcardsRef = collection(db, `data/digitalcard/cards/${cardId}/subcards`);
      const subcardsSnapshot = await getDocs(subcardsRef);
      
      console.log('🔍 [EmployeesService] Found', subcardsSnapshot.size, 'subcards');
      
      // Process each subcard
      for (const subcardDoc of subcardsSnapshot.docs) {
        const subcardData = subcardDoc.data();
        console.log('🔍 [EmployeesService] Processing subcard:', subcardDoc.id, subcardData);
        
        // Create employee object from subcard data
        const employee: Employee = {
          id: subcardData['id'] || subcardDoc.id,
          email: subcardData['email'] || '',
          fullName: subcardData['fullName'] || subcardData['name'] || '',
          handler: subcardData['handler'] || '',
          phone: subcardData['phone'] || subcardData['phoneNumber'] || '',
          profilePicture: subcardData['profilePicture'] || subcardData['photo'] || subcardData['avatar'] || '',
          department: subcardData['department'] || '',
          position: subcardData['position'] || subcardData['jobTitle'] || '',
          status: subcardData['status'] || 'active',
          joinDate: subcardData['joinDate'] || subcardData['dateOfJoining'] || '',
          createdAt: subcardData['createdAt'] || ''
        };
        
        employees.push(employee);
      }
      
      console.log('✅ [EmployeesService] Successfully loaded', employees.length, 'employees');
      return employees;
      
    } catch (error) {
      console.error('❌ [EmployeesService] Error getting employees:', error);
      throw error;
    }
  }

  async getEmployeeById(cardId: string, subcardId: string): Promise<Employee | null> {
    console.log('🔍 [EmployeesService] === GETTING EMPLOYEE BY ID ===');
    console.log('🔍 [EmployeesService] Card ID:', cardId);
    console.log('🔍 [EmployeesService] Subcard ID:', subcardId);
    
    try {
      const db = getFirestore();
      const subcardRef = doc(db, `data/digitalcard/cards/${cardId}/subcards`, subcardId);
      const subcardDoc = await getDoc(subcardRef);
      
      if (!subcardDoc.exists()) {
        console.warn('⚠️ [EmployeesService] Employee not found');
        return null;
      }
      
      const subcardData = subcardDoc.data();
      const employee: Employee = {
        id: subcardData['id'] || subcardDoc.id,
        email: subcardData['email'] || '',
        fullName: subcardData['fullName'] || '',
        handler: subcardData['handler'] || '',
        phone: subcardData['phone'] || '',
        profilePicture: subcardData['profilePicture'] || '',
        department: subcardData['department'] || '',
        position: subcardData['position'] || '',
        status: subcardData['status'] || 'active',
        joinDate: subcardData['joinDate'] || '',
        createdAt: subcardData['createdAt'] || ''
      };
      
      console.log('✅ [EmployeesService] Successfully loaded employee:', employee);
      return employee;
      
    } catch (error) {
      console.error('❌ [EmployeesService] Error getting employee:', error);
      throw error;
    }
  }

  async deleteEmployee(cardId: string, subcardId: string): Promise<boolean> {
    console.log('🗑️ [EmployeesService] === DELETING EMPLOYEE ===');
    console.log('🗑️ [EmployeesService] Card ID:', cardId);
    console.log('🗑️ [EmployeesService] Subcard ID:', subcardId);
    
    try {
      const db = getFirestore();
      const subcardRef = doc(db, `data/digitalcard/cards/${cardId}/subcards`, subcardId);
      
      // First check if the document exists
      const subcardDoc = await getDoc(subcardRef);
      if (!subcardDoc.exists()) {
        console.warn('⚠️ [EmployeesService] Employee document not found for deletion');
        return false;
      }
      
      // Delete the document
      await deleteDoc(subcardRef);
      console.log('✅ [EmployeesService] Employee deleted successfully');
      return true;
      
    } catch (error) {
      console.error('❌ [EmployeesService] Error deleting employee:', error);
      throw error;
    }
  }

  async findEmployeeCardId(userHandler: string, employeeId: string): Promise<string | null> {
    console.log('🔍 [EmployeesService] === FINDING EMPLOYEE CARD ID ===');
    console.log('🔍 [EmployeesService] User handler:', userHandler);
    console.log('🔍 [EmployeesService] Employee ID:', employeeId);
    
    try {
      const db = getFirestore();
      
      // First, search in users collection to get the user's email/details
      const usersRef = collection(db, 'users');
      const userQuery = query(usersRef, where('uid', '==', userHandler));
      const userSnapshot = await getDocs(userQuery);
      
      let userEmail = '';
      if (!userSnapshot.empty) {
        const userData = userSnapshot.docs[0].data();
        userEmail = userData['email'] || '';
      }
      
      // Now search for cards with the user's email or uid as handler
      const cardsRef = collection(db, 'data/digitalcard/cards');
      
      // Try multiple queries to find the user's card
      let cardId = '';
      
      // First try with the userHandler (uid)
      const cardQuery1 = query(cardsRef, where('handler', '==', userHandler));
      const cardSnapshot1 = await getDocs(cardQuery1);
      
      if (!cardSnapshot1.empty) {
        cardId = cardSnapshot1.docs[0].id;
      } else if (userEmail) {
        // Try with email if uid didn't work
        const cardQuery2 = query(cardsRef, where('handler', '==', userEmail));
        const cardSnapshot2 = await getDocs(cardQuery2);
        
        if (!cardSnapshot2.empty) {
          cardId = cardSnapshot2.docs[0].id;
        } else {
          // Try searching in card data for email field
          const cardQuery3 = query(cardsRef, where('email', '==', userEmail));
          const cardSnapshot3 = await getDocs(cardQuery3);
          
          if (!cardSnapshot3.empty) {
            cardId = cardSnapshot3.docs[0].id;
          }
        }
      }
      
      if (!cardId) {
        console.warn('⚠️ [EmployeesService] No card found for user');
        return null;
      }
      
      console.log('✅ [EmployeesService] Found card ID:', cardId);
      return cardId;
      
    } catch (error) {
      console.error('❌ [EmployeesService] Error finding employee card ID:', error);
      return null;
    }
  }

  async addEmployee(cardId: string, subcardId: string, employeeData: Employee): Promise<boolean> {
    console.log('➕ [EmployeesService] === ADDING EMPLOYEE ===');
    console.log('➕ [EmployeesService] Card ID:', cardId);
    console.log('➕ [EmployeesService] Subcard ID:', subcardId);
    console.log('➕ [EmployeesService] Employee data:', employeeData);
    
    try {
      const db = getFirestore();
      const subcardRef = doc(db, `data/digitalcard/cards/${cardId}/subcards`, subcardId);
      
      // Save the employee document
      await setDoc(subcardRef, employeeData);
      console.log('✅ [EmployeesService] Employee added successfully');
      return true;
      
    } catch (error) {
      console.error('❌ [EmployeesService] Error adding employee:', error);
      throw error;
    }
  }
}
